import type { FC } from "react";
import { useMemo, useState } from "react";

import dayjs from "dayjs";
import styled from "styled-components";

import { colorsPalettes, rgba } from "@similarweb/styles";
import { BasicModal, Button } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { afterTrialBannerModalStyles } from "app/common/after-trial/afterTrialBannerModalStyles";
import { isAfterTrialCCFromQuiz } from "app/common/after-trial/afterTrialSwSettingsPredicates";
import { swSettings } from "app/common/services/swSettings";
import { AfterTrialPopupContainer } from "app/components/React/AfterTrialPopup/AfterTrialPopupContainer";
import { i18nFilter } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";
import { allTrackers } from "app/services/track/track";

import reactivationIllustration from "./images/reactivation-banner-illustration.png";

// This test's trial length; the modal's own TRIAL_EXTENSION_DAYS (see StepPackagesCIAndSEODesktop/constants.ts) is unrelated.
const TRIAL_LENGTH_DAYS = 7;

const BannerWrapper = styled.div`
    background-color: ${colorsPalettes.carbon[0]};
    padding: 24px;
    box-shadow: 0 3px 6px ${rgba(colorsPalettes.carbon[500], 0.08)};
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    margin: 0 40px;
`;

const BlockText = styled(Text)`
    display: block;
`;

const StepsList = styled(Stack)`
    gap: 16px;
    width: 100%;
`;

const StepRow = styled(Flex)`
    gap: 8px;
    align-items: flex-start;
    position: relative;
`;

const StepConnector = styled.div<{ $color: string }>`
    position: absolute;
    /* top:24 (icon bottom) + bottom:-16 (StepsList gap) stretches the line down to the next icon,
       regardless of how tall this row's text makes it - a fixed height would fall short when the
       description wraps to two lines. */
    top: 24px;
    bottom: -16px;
    left: 11px;
    width: 2px;
    background-color: ${({ $color }) => $color};
`;

const StepIconCircle = styled(Flex)<{ $background: string }>`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    background-color: ${({ $background }) => $background};
    flex-shrink: 0;
    position: relative;
    z-index: 1;
`;

const StepIcon = styled.img`
    width: 16px;
    height: 16px;
`;

const StepTextContainer = styled(Stack)`
    gap: 4px;
    flex: 1;
    min-width: 0;
`;

const StyledButton = styled(Button)`
    align-self: flex-start;
`;

const ImgWrapper = styled.div`
    width: 217px;
    height: 219px;
    flex-shrink: 0;
    align-self: center;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Illustration = styled.img`
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
`;

const translate = i18nFilter();

interface IStep {
    iconUrl: string;
    background: string;
    connectorColor?: string;
    titleKey: string;
    titleParams?: Record<string, string>;
    descriptionKey: string;
    descriptionParams?: Record<string, string>;
}

export const AfterTrialBannerReactivationV2: FC = () => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const isSkipTrial = isAfterTrialCCFromQuiz(swSettings);

    const planStartDate = useMemo(() => dayjs().add(TRIAL_LENGTH_DAYS, "day").format("MMM D"), []);

    const steps: IStep[] = [
        {
            iconUrl: "/images/trial-modal/lightning.svg",
            background: colorsPalettes.blue[400],
            connectorColor: colorsPalettes.blue[400],
            titleKey: "aftertrial.homepage.banner.v2.step1.title",
            descriptionKey: "aftertrial.homepage.banner.v2.step1.sub.title",
        },
        {
            iconUrl: "/images/trial-modal/credit_card.svg",
            background: colorsPalettes.carbon[200],
            connectorColor: colorsPalettes.carbon[200],
            titleKey: "aftertrial.homepage.banner.v2.step2.title",
            descriptionKey: "aftertrial.homepage.banner.v2.step2.sub.title",
            descriptionParams: { date: planStartDate },
        },
        {
            iconUrl: "/images/trial-modal/rocket.svg",
            background: colorsPalettes.carbon[200],
            titleKey: "aftertrial.homepage.banner.v2.step3.title",
            titleParams: { date: planStartDate },
            descriptionKey: "aftertrial.homepage.banner.v2.step3.sub.title",
        },
    ];

    const onCtaClick = () => {
        allTrackers.trackEvent(
            "Internal Link",
            "click",
            isSkipTrial ? "Reactivate CC Trial/homepage/banner" : "Start CC Trial/homepage/banner",
        );
        setIsOpen(true);
    };

    return (
        <BannerWrapper data-automation="after-trial-banner-v2">
            <Stack gap="20px">
                <Stack gap="8px">
                    <BlockText
                        fontVariant="h5_2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {translate(
                            isSkipTrial
                                ? "aftertrial.homepage.banner.v2.skip.title"
                                : "aftertrial.homepage.banner.v2.title",
                        )}
                    </BlockText>
                    <BlockText
                        fontVariant="h6"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate("aftertrial.homepage.banner.v2.sub.title")}
                    </BlockText>
                </Stack>
                <StepsList>
                    {steps.map((step, index) => (
                        <StepRow key={step.titleKey}>
                            <StepIconCircle $background={step.background}>
                                <StepIcon
                                    src={AssetsService.assetUrl(step.iconUrl)}
                                    alt=""
                                />
                            </StepIconCircle>
                            {index < steps.length - 1 && step.connectorColor ? (
                                <StepConnector $color={step.connectorColor} />
                            ) : null}
                            <StepTextContainer>
                                <BlockText
                                    fontVariant="tab"
                                    color={colorsPalettes.carbon[500]}
                                >
                                    {translate(step.titleKey, step.titleParams)}
                                </BlockText>
                                <BlockText
                                    fontVariant="body2"
                                    color={colorsPalettes.carbon[300]}
                                >
                                    {translate(step.descriptionKey, step.descriptionParams)}
                                </BlockText>
                            </StepTextContainer>
                        </StepRow>
                    ))}
                </StepsList>
                <StyledButton
                    style={{ lineHeight: "normal" }}
                    onClick={onCtaClick}
                    data-automation="after-trial-banner-v2-cta"
                >
                    {translate(
                        isSkipTrial
                            ? "trial_banner.2.button.go.to.packages.v2.skip.text"
                            : "trial_banner.2.button.go.to.packages.v2.text",
                    )}
                </StyledButton>
            </Stack>
            <ImgWrapper>
                <Illustration
                    alt=""
                    src={reactivationIllustration}
                />
            </ImgWrapper>
            <BasicModal
                isOpen={isOpen}
                onRequestClose={() => setIsOpen(false)}
                closeIconConfig={{ show: false }}
                customStyles={afterTrialBannerModalStyles}
                shouldCloseOnOverlayClick={true}
            >
                <AfterTrialPopupContainer setIsOpen={setIsOpen} />
            </BasicModal>
        </BannerWrapper>
    );
};
